import { AfterContentChecked, Component, ElementRef, Input, OnChanges, OnInit, Renderer2, ViewChild } from '@angular/core';
import { SvgParser } from '@blk/simba';
import { ColorsConfig } from '@ddt-web/config';
import { ComponentData, TabData } from '@ddt-web/data-service';
import * as C2S from 'canvas2svg';
import * as get from 'lodash/get';
import * as isNumber from 'lodash/isNumber';

import { TooltipOffset } from './model/style-box.interface';
import { StyleBoxTooltipComponent } from './tooltip/style-box-tooltip.component';

@Component({
	selector: 'ac-style-box',
	templateUrl: './style-box.component.html',
	styleUrls: ['./style-box.component.scss']
})
export class StyleBoxComponent implements OnInit, OnChanges, AfterContentChecked {
	@Input() public data: TabData;
	@Input() public type: string;
	@Input() public contributionTooltipData: TabData;
	@Input() public customTooltipOffset?: TooltipOffset;
	@Input() public colors?: string[][];
	@Input() public productNameCharacterLimit?: number;
	@Input() public tooltipWidth?: number;

	@ViewChild('chartCanvas1', { static: false }) public chartCanvasRef: ElementRef;
	@ViewChild('chartCanvas2', { static: false }) public chart2CanvasRef: ElementRef;
	@ViewChild('chartCanvas3', { static: false }) public chart3CanvasRef: ElementRef;
	@ViewChild('chartCanvas4', { static: false }) public chart4CanvasRef: ElementRef;
	@ViewChild('chartContainer', { static: false }) public container: ElementRef;
	@ViewChild('contributionTooltip') private styleBoxTooltipComponent: StyleBoxTooltipComponent;

	public noOfPortfolios: number;
	private coordinates: ChartCoordinate[] = [
		new ChartCoordinate('largeValue', 25, 25),
		new ChartCoordinate('largeBlend', 85, 25),
		new ChartCoordinate('largeGrowth', 140, 25),
		new ChartCoordinate('midValue', 25, 85),
		new ChartCoordinate('midBlend', 85, 85),
		new ChartCoordinate('midGrowth', 140, 85),
		new ChartCoordinate('smallValue', 25, 140),
		new ChartCoordinate('smallBlend', 85, 140),
		new ChartCoordinate('smallGrowth', 140, 140)
	];
	private valueRange: number[] = [49, 24, 9, 0];
	private pi2 = Math.PI * 2;
	private radius = 25;
	private fontSize = 5;
	private padding = 40;
	private indicatorLineGap = 10;
	private arrowWidth = 5;
	public itemColumnClass: string;
	public tabData: TabData;
	private chart1CtxSVG: any;
	private chart2CtxSVG: any;
	private chart3CtxSVG: any;
	private chart4CtxSVG: any;
	public height: string = '40.5';

	constructor(public renderer?: Renderer2) { }

	public ngOnInit(): void {
		// TODO waris: Remove the timeout hack after the solution to load the font before script is running
		setTimeout(() => {
			this.initializeCanvas();
			this.noOfPortfolios = this.data.tableData[0].portfolioData.length;
			const chartCanvases: ElementRef[] = [this.chartCanvasRef, this.chart2CanvasRef, this.chart3CanvasRef, this.chart4CanvasRef];
			for (let chartNum = 0; chartNum < this.noOfPortfolios; chartNum++) {
				this.displayChart(chartCanvases[chartNum], chartNum, '250', '250');
			}
		}, 500);
	}

	public ngAfterContentChecked(): void {
		/** Dynamic height of the the header is calculated because it varies with changing height of dl-display table header **/
		const sibling: any =
			get(this.container, 'nativeElement.parentElement') &&
			(this.container.nativeElement.parentElement.previousElementSibling || this.container.nativeElement.parentElement.nextElementSibling);
		if (get(sibling, 'tagName', '').toLowerCase() === 'dl-display-table') {
			this.height = (sibling.querySelector('header').offsetHeight - 1.5) + 'px';
		}

	}

	public ngOnChanges(): void {
		if (this.data instanceof ComponentData) {
			this.tabData = this.data && this.data.tabs && this.data.tabs[0];
		} else {
			this.tabData = this.data;
		}
		this.assignColumnClasses(get(this.tabData, 'tableData[0].headersX.length', 0));
	}

	public initializeCanvas(): void {
		this.chart1CtxSVG = new C2S(250, 250);
		this.chart2CtxSVG = new C2S(250, 250);
		this.chart3CtxSVG = new C2S(250, 250);
		this.chart4CtxSVG = new C2S(250, 250);
		this.noOfPortfolios = this.data.tableData[0].portfolioData.length;
		const chartCanvasesSVG: any[] = [this.chart1CtxSVG, this.chart2CtxSVG, this.chart3CtxSVG, this.chart4CtxSVG];
		for (let chartNum = 0; chartNum < this.noOfPortfolios; chartNum++) {
			this.drawBubbleChart(chartCanvasesSVG[chartNum], chartNum);
		}
	}

	public convertCanvasToSVG(): any[] {
		const svgs = [];
		svgs.push(this.chart1CtxSVG.getSerializedSvg(true));
		svgs.push(this.chart2CtxSVG.getSerializedSvg(true));
		svgs.push(this.chart3CtxSVG.getSerializedSvg(true));
		svgs.push(this.chart4CtxSVG.getSerializedSvg(true));
		return svgs;
	}

	public displayChart(chartCanvasRef: ElementRef, chartNumber: number, width: string, height: string): void {
		const parser: SvgParser = new SvgParser();
		const svgElement = this.renderer.createElement('svg', 'svg');
		this.renderer.setAttribute(svgElement, 'width', width);
		this.renderer.setAttribute(svgElement, 'height', height);
		svgElement.appendChild(parser.parseG(this.convertCanvasToSVG()[chartNumber]));
		this.renderer.appendChild(chartCanvasRef.nativeElement, svgElement);
	}

	private drawBubbleChart(context: any, chartNumber: number): void {
		this.drawCircles(context, this.coordinates, chartNumber, this.radius, this.pi2);
		this.drawCircleValues(context, this.coordinates, chartNumber);
		this.drawIndicators(context, this.coordinates);
	}

	private drawCircles(ctx: CanvasRenderingContext2D, coordinates: ChartCoordinate[], chartNumber: number, radius: number, endAngle: number): void {
		ctx.save();
		coordinates.forEach(coordinate => {
			const tableCells = get(this.data, 'tableData[0].cells', []);
			const filteredCells = tableCells.filter(cell => get(cell, `[${chartNumber}].config.id`) === coordinate.name);
			let displayValue = get(filteredCells, `[0][${chartNumber}].formattedValue`);
			const rawValue = get(filteredCells, `[0][${chartNumber}].rawValue`);
			if (isNumber(rawValue) && displayValue && displayValue !== '-') {
				displayValue = Math.round(rawValue).toString();
			}
			ctx.beginPath();
			const colourConfigIndex = this.valueRange.findIndex(range => displayValue > range);
			const colorsToUse = this.colors || ColorsConfig.EQUITY_STYLE_COLORS;
			const circleColor = colourConfigIndex > -1 ? colorsToUse[chartNumber][colourConfigIndex] : colorsToUse[chartNumber][3];
			ctx.fillStyle = circleColor;
			ctx.moveTo(coordinate.x + this.padding, coordinate.y + this.padding);
			ctx.arc(coordinate.x + this.padding, coordinate.y + this.padding, radius, 0, endAngle);
			ctx.fill();
		});
		ctx.restore();
	}

	private drawCircleValues(ctx: CanvasRenderingContext2D, coordinates: ChartCoordinate[], chartNumber: number): void {
		ctx.beginPath();
		coordinates.forEach(coordinate => {
			const tableCells = get(this.data, 'tableData[0].cells', []);
			const filteredCells = tableCells.filter(cell => get(cell, `[${chartNumber}].config.id`) === coordinate.name);
			// displayValue can be either an integer or hyphen(-)
			let displayValue = get(filteredCells, `[0][${chartNumber}].formattedValue`);
			const rawValue = get(filteredCells, `[0][${chartNumber}].rawValue`);
			if (isNumber(rawValue) && displayValue && displayValue !== '-') {
				displayValue = Math.round(rawValue).toString();
			}
			ctx.save();
			const textColor = Number(displayValue) > this.valueRange[1] && chartNumber < 2 ? 'white' : 'black';
			ctx.textAlign = 'center';
			ctx.fillStyle = textColor;
			ctx.font = '14px FortBook';
			ctx.fillText(displayValue, coordinate.x + this.padding, coordinate.y + this.fontSize + this.padding);
			ctx.restore();
		});
	}

	private drawIndicators(ctx: CanvasRenderingContext2D, coordinates: ChartCoordinate[]): void {
		ctx.save();
		this.drawTextIndicators(ctx, [coordinates[0], coordinates[2]]);
		this.drawLines(ctx, 40, 40, coordinates);
		this.drawArrows(ctx, 40, 40, coordinates);
		this.drawArrows(ctx, 100, 40, coordinates, 180);
		ctx.restore();
		ctx.save();
		this.drawTextIndicators(ctx, [coordinates[0], coordinates[2]], -90);
		this.drawLines(ctx, 40, 40, coordinates, -90);
		this.drawArrows(ctx, -25, 110, coordinates, 90);
		this.drawArrows(ctx, -25, 170, coordinates, -90);
		ctx.restore();
	}

	private drawTextIndicators(ctx: CanvasRenderingContext2D, coordinates: ChartCoordinate[], angle: number = 0): void {
		ctx.save();
		ctx.beginPath();
		ctx.textAlign = 'center';
		ctx.fillStyle = 'black';
		ctx.font = '400 14px FortBook';
		if (angle !== 0) {
			ctx.translate(230, 10);
			ctx.rotate((angle * Math.PI) / 180);
			ctx.translate(-230, -10);
			ctx.fillText('Small', coordinates[0].x + this.padding - this.indicatorLineGap, this.padding - this.indicatorLineGap - 220);
			ctx.fillText('Large', coordinates[1].x + this.padding - this.indicatorLineGap, this.padding - this.indicatorLineGap - 220);
		} else {
			ctx.fillText('Value', coordinates[0].x + this.padding, this.padding - this.indicatorLineGap);
			ctx.fillText('Growth', coordinates[1].x + this.padding, this.padding - this.indicatorLineGap);
		}
		ctx.restore();
	}

	private drawLines(ctx: CanvasRenderingContext2D, abscissa: number, ordinate: number, coordinates: ChartCoordinate[], angle: number = 0): void {
		ctx.save();
		ctx.beginPath();
		if (angle !== 0) {
			ctx.translate(abscissa + 2 * coordinates[0].x, ordinate - this.indicatorLineGap - this.fontSize);
			ctx.rotate((angle * Math.PI) / 180);
			ctx.translate(-(abscissa + 2 * coordinates[0].x), -(ordinate - this.indicatorLineGap - this.fontSize));
			ctx.moveTo(abscissa + 2 * coordinates[0].x - 130, ordinate - this.indicatorLineGap - this.fontSize - 65);
			ctx.lineTo(abscissa + coordinates[0].x + coordinates[1].x - 130, ordinate - this.indicatorLineGap - this.fontSize - 65);
		} else {
			ctx.moveTo(abscissa + 2 * coordinates[0].x, ordinate - this.indicatorLineGap - this.fontSize);
			ctx.lineTo(abscissa + coordinates[0].x + coordinates[1].x, ordinate - this.indicatorLineGap - this.fontSize);
		}
		ctx.strokeStyle = 'grey';
		ctx.stroke();
		ctx.restore();
	}

	private assignColumnClasses(count: number): void {
		// TODO dushyant: mobile view need refactoring
		switch (count) {
			case 1:
				this.itemColumnClass = 'col-24';
				break;
			case 2:
				this.itemColumnClass = 'col-12';
				break;
			case 3:
				this.itemColumnClass = 'col-8';
				break;
		}
	}

	private drawArrows(ctx: CanvasRenderingContext2D, abscissa: number, ordinate: number, coordinates: ChartCoordinate[], arrowAngle: number = 0): void {
		ctx.save();
		const arrowSize = this.arrowWidth;
		ctx.beginPath();
		ctx.fillStyle = 'grey';
		if (arrowAngle !== 0) {
			ctx.translate(abscissa + 2 * coordinates[0].x, ordinate - this.indicatorLineGap - this.fontSize);
			ctx.rotate((arrowAngle * Math.PI) / 180);
			ctx.translate(-(abscissa + 2 * coordinates[0].x), -(ordinate - this.indicatorLineGap - this.fontSize));
		}
		ctx.moveTo(abscissa + 2 * coordinates[0].x, ordinate - this.indicatorLineGap - this.fontSize);
		ctx.lineTo(abscissa + 2 * coordinates[0].x + arrowSize, ordinate - this.indicatorLineGap - arrowSize - this.fontSize);
		ctx.lineTo(abscissa + 2 * coordinates[0].x + arrowSize, ordinate - this.indicatorLineGap + arrowSize - this.fontSize);
		ctx.fill();
		ctx.restore();
	}

	public onMouseEvent($event, selectedPortfolioIndex): void {
		if ($event) {
			this.styleBoxTooltipComponent.onMouseEvent($event, selectedPortfolioIndex);
		}
	}
}

class ChartCoordinate {
	public name: string;
	public x: number;
	public y: number;

	constructor(name, x, y) {
		this.name = name;
		this.x = x;
		this.y = y;
	}
}
